import { useState, useRef, useCallback, useEffect } from "react";
import { Button, Space, Grid, Spin, Tooltip, theme, Result } from "antd";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { ENV } from "../../config/env";

const { useBreakpoint } = Grid;

const DARK_MODE_STYLE_ID = "manual-dark-mode";

const DARK_MODE_CSS = `
  body {
    background: #141414 !important;
    color: #e0e0e0 !important;
  }
  h1 { color: #5b9cf5 !important; border-bottom-color: #5b9cf5 !important; }
  h2 { color: #66bb6a !important; border-bottom-color: #2e5232 !important; }
  h3 { color: #ffab40 !important; }
  a { color: #5b9cf5 !important; }
  table { border-color: #333 !important; }
  th { background: #1a3a5c !important; color: #e0e0e0 !important; }
  td { border-color: #333 !important; color: #ccc !important; }
  tr:nth-child(even) { background: #1e1e1e !important; }
  tr:hover { background: #1a2a3a !important; }
  blockquote {
    background: #2a2000 !important;
    border-left-color: #ff9800 !important;
    color: #e0c870 !important;
  }
  code { background: #2a2a2a !important; color: #d4d4d4 !important; }
  #TOC {
    background: #1e1e1e !important;
    border-color: #333 !important;
  }
  #TOC a { color: #5b9cf5 !important; }
  img, svg { opacity: 0.9; }
`;

function injectDarkMode(iframe: HTMLIFrameElement, isDark: boolean) {
  try {
    const doc = iframe.contentDocument;
    if (!doc) return;

    const existing = doc.getElementById(DARK_MODE_STYLE_ID);
    if (isDark && !existing) {
      const style = doc.createElement("style");
      style.id = DARK_MODE_STYLE_ID;
      style.textContent = DARK_MODE_CSS;
      doc.head.appendChild(style);
    } else if (!isDark && existing) {
      existing.remove();
    }
  } catch {
    // cross-origin — cannot access iframe document
  }
}

/**
 * User Manual viewer page.
 * Loads the HTML file at /manuals/{VITE_DASHBOARD_TYPE}.html inside an iframe.
 * Injects dark mode CSS when the dashboard theme is dark.
 */
export const UserManuals: React.FC = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { token } = theme.useToken();
  const { isDark } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const isMobile = !screens.md;
  const manualFile = `/manuals/${ENV.DASHBOARD_TYPE}.html`;

  const handleLoad = useCallback(() => {
    setLoading(false);
    if (iframeRef.current) {
      injectDarkMode(iframeRef.current, isDark);
    }
  }, [isDark]);

  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  // Re-inject when theme toggles after iframe is already loaded
  useEffect(() => {
    if (!loading && iframeRef.current) {
      injectDarkMode(iframeRef.current, isDark);
    }
  }, [isDark, loading]);

  // Escape to exit fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [fullscreen]);

  if (error) {
    return (
      <Result
        status="404"
        title={t("manuals.notFound")}
        subTitle={t("manuals.notFoundHint", {
          file: `public/manuals/${ENV.DASHBOARD_TYPE}.html`,
        })}
      />
    );
  }

  const toolbar = (
    <div
      style={{
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        backgroundColor: token.colorBgContainer,
        flexShrink: 0,
      }}
    >
      <Space size="middle">
        <BookOutlined style={{ fontSize: 18 }} />
        <span style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>
          {t("manuals.title")}
        </span>
      </Space>
      {!isMobile && (
        <Tooltip
          title={
            fullscreen ? t("manuals.exitFullscreen") : t("manuals.fullscreen")
          }
        >
          <Button
            type="text"
            icon={
              fullscreen ? (
                <FullscreenExitOutlined />
              ) : (
                <FullscreenOutlined />
              )
            }
            onClick={() => setFullscreen((prev) => !prev)}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div
      style={{
        position: fullscreen ? "fixed" : "relative",
        inset: fullscreen ? 0 : undefined,
        zIndex: fullscreen ? 1050 : undefined,
        display: "flex",
        flexDirection: "column",
        height: fullscreen ? "100vh" : "calc(100vh - 64px - 24px)",
        backgroundColor: token.colorBgContainer,
        borderRadius: fullscreen ? 0 : token.borderRadiusLG,
        overflow: "hidden",
      }}
    >
      {toolbar}
      <div style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              backgroundColor: token.colorBgContainer,
            }}
          >
            <Spin size="large" />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={manualFile}
          onLoad={handleLoad}
          onError={handleError}
          title={t("manuals.title")}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </div>
    </div>
  );
};
